/**
 * Learn more about theme events at
 * https://help.switchthemes.co/shapes/developer/theme-events/overview.html
 */

window.theme = window.theme || {};

window.theme.events = {
  version: '1.0',
};

// LibertyPaw 2026-09-24 (audit item A1): cart refresh reliability.
// A newer refresh cancels an older one, a 15 s limit applies (GET only, never a
// cart mutation), and the response is checked before the drawer is re-rendered,
// so a failed refresh shows the cart error instead of locking up or going stale.
// (ported to v5 staging 2026-09-28: v5 asks utils.js getCartSectionRequestIds()
// which sections to request -- the static cart sections, or the cart-drawer section
// + count + live region -- so every requested section is checked, and the items /
// footer markup is checked through v5's own getCartSectionHTML() for either mode.)
let cartRefreshRevision = 0;
let cartRefreshController;
let cartRefreshErrorTargets = [];

document.addEventListener('theme:update:cart', async () => {
  const revision = ++cartRefreshRevision;
  cartRefreshController?.abort();
  const controller = new AbortController();
  cartRefreshController = controller;
  let timeout;
  document.dispatchEvent(new CustomEvent('shapes:cart:lock'));

  try {
    const sectionIds = getCartSectionRequestIds();
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds.join(','));
    url.hash = '';
    const data = await Promise.race([
      (async () => {
        const response = await fetch(url.pathname + url.search, { signal: controller.signal });
        if (!response.ok) throw new Error('Cart refresh HTTP error');
        return response.json();
      })(),
      new Promise((_, reject) => {
        timeout = setTimeout(() => {
          reject(new Error('Cart refresh timed out'));
          controller.abort();
        }, 15000);
      }),
    ]);
    if (revision !== cartRefreshRevision) return;
    if (!data || typeof data !== 'object' || Array.isArray(data) || data.status) {
      throw new Error('Invalid cart refresh response');
    }
    if (sectionIds.some((name) => typeof data[name] !== 'string' || !data[name].trim())) {
      throw new Error('Missing cart refresh section');
    }
    const countHTML = getCartSectionHTML(data, 'count');
    if (
      !getCartSectionHTML(data, 'items') ||
      !getCartSectionHTML(data, 'footer') ||
      !countHTML ||
      !/^\d+$/.test(parseDOMFromString(countHTML).body.textContent.trim())
    ) {
      throw new Error('Invalid cart refresh markup');
    }
    cartRefreshErrorTargets.forEach(({ element, message }) => {
      if (element.isConnected && element.textContent === message) element.textContent = '';
    });
    cartRefreshErrorTargets = [];
    document.dispatchEvent(
      new CustomEvent('shapes:cart:update', {
        bubbles: true,
        detail: {
          response: {
            sections: {
              ...data,
            },
          },
        },
      })
    );
  } catch (e) {
    if (revision !== cartRefreshRevision) return;
    console.error(e);
    const message = window.theme.strings.cartError;
    cartRefreshErrorTargets = Array.from(document.querySelectorAll('[id="cart-errors"]')).map((element) => {
      element.textContent = message;
      return { element, message };
    });
    document.dispatchEvent(
      new CustomEvent('theme:cart:error:other', {
        detail: {
          message,
          error: e,
        },
      })
    );
  } finally {
    clearTimeout(timeout);
    if (revision === cartRefreshRevision) {
      cartRefreshController = null;
      document.dispatchEvent(new CustomEvent('shapes:cart:unlock'));
    }
  }
});

const getCartDrawerEl = () => {
  return document.querySelector('[data-cart-drawer]');
};

document.addEventListener('cart-will-open', async () => {
  const cartDrawerEl = getCartDrawerEl();

  const detail = {
    cartDrawerEl,
  };

  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:opening', {
      detail,
    })
  );
});

document.addEventListener('cart-is-open', async () => {
  const cartDrawerEl = getCartDrawerEl();

  const detail = {
    cartDrawerEl,
  };

  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:open', {
      detail,
    })
  );
});

document.addEventListener('cart-will-close', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closing', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('cart-is-closed', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closed', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('theme:open:cart-drawer', () => {
  // A1: null-safe if Alpine or the modals store is not ready yet.
  // (ported to v5 staging 2026-09-28; v5 reads settings.cartType, which js-bridge defines.)
  const modals = window.Alpine?.store('modals');
  if (
    window.theme.settings?.cartType === 'modal' &&
    modals?.modals?.cart
  ) {
    modals.closeAll();

    modals.open('cart');
  }
});

document.addEventListener('theme:close:cart-drawer', () => {
  const modals = window.Alpine?.store('modals');
  if (
    window.theme.settings?.cartType === 'modal' &&
    modals?.modals?.cart
  ) {
    modals.close('cart');
  }
});

const cartChangeEventHandler = (e) => {
  const { cartItemCount, itemsRootEl, lineItemEl, variantId, key } = e.detail;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      detail: {
        type: e.type,
        cartItemCount,
        itemsRootEl,
        lineItemEl,
        variantId,
        key,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener('theme:product:add', cartChangeEventHandler);
document.addEventListener('theme:line-item:change', cartChangeEventHandler);

const cartErrorEventHandler = (e) => {
  document.dispatchEvent(
    new CustomEvent('theme:cart:error', {
      detail: {
        type: e.type,
        message: e.detail.message,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener(
  'theme:product:error:add-to-cart',
  cartErrorEventHandler
);

document.addEventListener('theme:line-item:error', cartErrorEventHandler);
document.addEventListener('theme:cart:error:other', cartErrorEventHandler);
